import { Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import { Router } from "@angular/router";
import { RealtimeChannel } from "@supabase/supabase-js";
import { BehaviorSubject, Observable, Subscription, interval } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";

import { SupabaseService } from "src/app/core/supabase.service";
import { AuthenticationService } from "src/app/members/auth/services/authentication.service";
import { AppNotification } from "./notification.models";

const PAGE_SIZE = 50;

/**
 * The signed-in user's notification inbox.
 *
 * Loads the latest notifications when someone signs in, then keeps the list
 * live over Supabase Realtime. Rows are only ever created by database
 * triggers; the client can read them, mark them read and delete them.
 */
@Injectable({ providedIn: "root" })
export class NotificationsService implements OnDestroy {
    private readonly supabase = inject(SupabaseService).client;
    private readonly auth = inject(AuthenticationService);
    private readonly router = inject(Router);
    private readonly zone = inject(NgZone);

    private readonly items = new BehaviorSubject<AppNotification[]>([]);
    private readonly loaded = new BehaviorSubject<boolean>(false);
    private channel: RealtimeChannel | null = null;
    private userId: string | null = null;
    private readonly authSub: Subscription;

    readonly notifications$: Observable<AppNotification[]> = this.items.asObservable();
    readonly loaded$: Observable<boolean> = this.loaded.asObservable();
    readonly unreadCount$: Observable<number> = this.items.pipe(
        map((list) => list.filter((n) => !n.read_at).length),
        distinctUntilChanged()
    );

    /** Ticks every minute so "5 minutes ago" labels stay honest. */
    readonly now$: Observable<number> = interval(60_000).pipe(
        startWith(0),
        map(() => Date.now())
    );

    constructor() {
        this.authSub = this.auth.user$
            .pipe(
                map((user) => user?.id ?? null),
                distinctUntilChanged()
            )
            .subscribe((id) => void this.switchUser(id));
    }

    ngOnDestroy(): void {
        this.authSub.unsubscribe();
        void this.teardown();
    }

    // ------------------------------------------------------------ actions

    async markRead(notification: AppNotification): Promise<void> {
        if (notification.read_at) {
            return;
        }
        const readAt = new Date().toISOString();
        this.patchLocal(notification.id, { read_at: readAt });

        const { error } = await this.supabase
            .from("notifications")
            .update({ read_at: readAt })
            .eq("id", notification.id);
        if (error) {
            console.error("Could not mark notification read", error);
            this.patchLocal(notification.id, { read_at: null });
        }
    }

    async markAllRead(): Promise<void> {
        if (!this.userId) {
            return;
        }
        const readAt = new Date().toISOString();
        const before = this.items.value;
        this.items.next(before.map((n) => (n.read_at ? n : { ...n, read_at: readAt })));

        const { error } = await this.supabase
            .from("notifications")
            .update({ read_at: readAt })
            .eq("user_id", this.userId)
            .is("read_at", null);
        if (error) {
            console.error("Could not mark notifications read", error);
            this.items.next(before);
        }
    }

    async remove(notification: AppNotification): Promise<void> {
        const before = this.items.value;
        this.items.next(before.filter((n) => n.id !== notification.id));

        const { error } = await this.supabase
            .from("notifications")
            .delete()
            .eq("id", notification.id);
        if (error) {
            console.error("Could not delete notification", error);
            this.items.next(before);
        }
    }

    async clearRead(): Promise<void> {
        if (!this.userId) {
            return;
        }
        const before = this.items.value;
        this.items.next(before.filter((n) => !n.read_at));

        const { error } = await this.supabase
            .from("notifications")
            .delete()
            .eq("user_id", this.userId)
            .not("read_at", "is", null);
        if (error) {
            console.error("Could not clear notifications", error);
            this.items.next(before);
        }
    }

    /** Mark read, then follow the link (highlighting the registration it's about). */
    async open(notification: AppNotification): Promise<void> {
        void this.markRead(notification);
        if (!notification.link) {
            return;
        }
        const highlight =
            notification.registration_id && notification.link.startsWith("/admin/")
                ? { highlight: notification.registration_id }
                : undefined;
        await this.router.navigate([notification.link], { queryParams: highlight });
    }

    // ------------------------------------------------------------ plumbing

    private async switchUser(userId: string | null): Promise<void> {
        await this.teardown();
        this.userId = userId;
        this.items.next([]);
        this.loaded.next(false);

        if (!userId) {
            return;
        }

        const { data, error } = await this.supabase
            .from("notifications")
            .select("*")
            .order("created_at", { ascending: false })
            .limit(PAGE_SIZE);

        // The user may have signed out while we were waiting.
        if (this.userId !== userId) {
            return;
        }
        if (error) {
            console.error("Could not load notifications", error);
        } else {
            this.items.next((data as AppNotification[]) ?? []);
        }
        this.loaded.next(true);

        this.channel = this.supabase
            .channel(`notifications:${userId}`)
            .on<AppNotification>(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "notifications",
                    filter: `user_id=eq.${userId}`,
                },
                (payload) =>
                    this.zone.run(() => {
                        const row = payload.new as AppNotification;
                        if (!this.items.value.some((n) => n.id === row.id)) {
                            this.items.next([row, ...this.items.value].slice(0, PAGE_SIZE));
                        }
                    })
            )
            .on<AppNotification>(
                "postgres_changes",
                {
                    event: "UPDATE",
                    schema: "public",
                    table: "notifications",
                    filter: `user_id=eq.${userId}`,
                },
                (payload) =>
                    this.zone.run(() => {
                        const row = payload.new as AppNotification;
                        this.patchLocal(row.id, row);
                    })
            )
            .subscribe();
    }

    private async teardown(): Promise<void> {
        if (this.channel) {
            const channel = this.channel;
            this.channel = null;
            await this.supabase.removeChannel(channel);
        }
    }

    private patchLocal(id: string, patch: Partial<AppNotification>): void {
        this.items.next(this.items.value.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    }
}
